import {
    FieldTree,
    PathKind,
    SchemaPath,
    SchemaPathRules,
    ValidationError,
    validateTree,
} from '@angular/forms/signals';

/** A span of time, as these rules compare it. */
export interface TimeSpanItem {
    /** ISO instant, or '' when unset */
    start: string;
    /** ISO instant, or '' when unset */
    end: string;
}

/**
 * The two fields that edit one time span.
 *
 * The span rules are handed fields rather than values because an error has to land somewhere the
 * user can see it: a rule that only knew the instants could say the collection is out of order,
 * but not mark the row that is. Since a span is edited as two fields, the rule can name the one at
 * fault — the start that reaches back, or the end that reaches forward.
 */
export interface TimeSpanFields {
    /** the field holding the span's start as an ISO instant */
    start: FieldTree<string>;
    /** the field holding the span's end as an ISO instant */
    end: FieldTree<string>;
}

/**
 * One disagreement between spans: which instant is at fault, and what to call it.
 *
 * The span rules are split into a pure search that produces these and a thin wrapper that turns
 * them into field errors. That split is what lets a caller ask the same question of a plain value
 * — "would this edit put the collection out of order?" — without building a form to ask it, and
 * without a second implementation of the comparison drifting away from the one the form uses.
 */
export interface SpanConflict {
    /** the item at fault, or undefined when the outer bound is the one being marked */
    index?: number;
    /** which of the span's two instants is at fault */
    half: keyof TimeSpanItem;
    /** the error kind to report */
    kind: string;
    /**
     * The instant the offending value may not cross — the neighbouring end it reaches into, or the
     * bound it falls outside of.
     *
     * Carried so a message can name it. "Choose a time no later than 11:00" tells the user what to
     * do; "the timesheet is invalid" leaves them to work out which of two values to change and by
     * how much, on a page where several rows may be marked at once.
     */
    limit: string;
    /** whether the offending value must not fall before or after {@link limit} */
    comparison: 'notBefore' | 'notAfter';
}

/**
 * Builds the message for one conflict.
 *
 * A function rather than a string per error kind, because the wording belongs to the caller — it
 * knows the translations and how the user's locale writes an instant — while which conflicts exist
 * belongs to the rule. It also keeps the rules from growing an option per kind as kinds are added.
 */
export type SpanConflictMessage = (conflict: SpanConflict) => string;

/**
 * A conflict that names an item, which every disagreement between items does — only an outer bound
 * has no index.
 */
export type ItemSpanConflict = SpanConflict & { index: number };

/** Options of the {@link chronologicallyOrdered} rule. */
export interface ChronologicallyOrderedConfig {
    /** builds the message for each conflict found */
    message?: SpanConflictMessage;
}

/**
 * Milliseconds of an ISO instant, or undefined when it is unset or unparsable.
 *
 * @param isoTimestamp the instant to parse
 * @returns the epoch milliseconds, or undefined
 */
export const spanEpoch = (isoTimestamp: string): number | undefined => {
    if (!isoTimestamp) {
        return undefined;
    }
    const parsed = Date.parse(isoTimestamp);
    return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * The value of one span's two fields.
 *
 * @param fields the span's two fields
 * @returns the instants they hold
 */
export const spanFieldsValue = (fields: TimeSpanFields): TimeSpanItem => ({
    start: fields.start().value(),
    end: fields.end().value(),
});

/**
 * The error a span rule reports on the validated collection itself, alongside the ones it reports on
 * the individual fields at fault.
 *
 * Needed because **a readonly field carries no validation state at all** — Angular drops it, so a
 * conflict reported only on readonly fields would neither show a message nor make the form invalid.
 * Both halves of a disagreement can be readonly at once: two entries a confirmation recorded, whose
 * times the timesheet does not let anyone edit, can still overlap each other. Reported here, the
 * form counts the conflict whatever the fields do with it.
 *
 * Invisible in itself, since a collection is not bound to a control. The field errors are what the
 * user reads; this is what the form counts.
 *
 * @param message the message to carry
 * @returns the collection-level error
 */
export const spanConflictSummary = (message: string): ValidationError.WithOptionalFieldTree => ({
    kind: 'spanConflict',
    message,
});

/**
 * Every way a collection of time spans is out of order: an item whose end lies before its start,
 * and an item that begins before its predecessor ended.
 *
 * Items are compared in the order given, and every offender is reported rather than only the first,
 * so a row can never be left unmarked because another row is wrong too.
 *
 * **An overlap marks both ends of itself** — the end that reaches forward and the start it reaches
 * into. Neither is the one at fault: the two values contradict each other, and either could be the
 * one to change. Marking only the later item would put the message somewhere the user never touched,
 * and somewhere they may not be allowed to touch: a timesheet's confirmation entries are read-only,
 * so extending a manual entry's end over one of them would otherwise be reported on a field that
 * shows nothing.
 *
 * An unset or unparsable instant is passed over rather than reported: whether an empty value is
 * acceptable is a `required` question, and reporting both at once would show the user two errors
 * for one omission.
 *
 * @param items the spans to compare, in the order they are to be compared
 * @returns one conflict per offending instant, in collection order
 */
export function findOrderConflicts(items: readonly TimeSpanItem[]): ItemSpanConflict[] {
    const conflicts: ItemSpanConflict[] = [];

    items.forEach((item, index) => {
        const start = spanEpoch(item.start);
        const end = spanEpoch(item.end);

        // The end is what is marked: the start is where the span was said to begin, so the end is
        // the value that contradicts it.
        if (start !== undefined && end !== undefined && end < start) {
            conflicts.push({
                index,
                half: 'end',
                kind: 'invertedSpan',
                limit: item.start,
                comparison: 'notBefore',
            });
        }

        const previous = index > 0 ? items[index - 1] : undefined;
        const previousEnd = previous ? spanEpoch(previous.end) : undefined;
        if (start !== undefined && previous !== undefined && previousEnd !== undefined && start < previousEnd) {
            conflicts.push(
                {
                    index: index - 1,
                    half: 'end',
                    kind: 'overlappingSpans',
                    limit: item.start,
                    comparison: 'notAfter',
                },
                {
                    index,
                    half: 'start',
                    kind: 'overlappingSpans',
                    limit: previous.end,
                    comparison: 'notBefore',
                },
            );
        }
    });

    return conflicts;
}

/**
 * Signal-forms schema rule for a collection of time spans: every item must end no earlier than it
 * starts, and no item may begin before its predecessor ended.
 *
 * Validated across the whole collection rather than per item, because "before its predecessor"
 * cannot be decided from one item alone — and because doing it here means the comparison always
 * uses the current sibling values rather than a copy taken when the form was seeded.
 *
 * Each error names the field at fault, which is what puts the message on the row the user is
 * editing instead of only at the foot of the page, plus one on the collection so that the form's
 * own validity does not rest on those fields — see {@link spanConflictSummary} for why that matters.
 * {@link findOrderConflicts} is where the comparison itself lives.
 *
 * @param path the collection to validate
 * @param toItems reads the fields of each item, in the order they are to be compared
 * @param config optional messages
 */
export function chronologicallyOrdered<TValue, TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<TValue, SchemaPathRules.Supported, TPathKind>,
    toItems: (fieldTree: FieldTree<TValue>) => readonly TimeSpanFields[],
    config?: ChronologicallyOrderedConfig,
): void {
    validateTree(path, (context) => {
        const items = toItems(context.fieldTree);
        const message = config?.message ?? ((): string => '');

        const conflicts = findOrderConflicts(items.map(spanFieldsValue));
        const errors = conflicts.map((conflict): ValidationError.WithOptionalFieldTree => ({
            kind: conflict.kind,
            message: message(conflict),
            fieldTree: items[conflict.index][conflict.half],
        }));

        return conflicts.length === 0 ? errors : [...errors, spanConflictSummary(message(conflicts[0]))];
    });
}
